import { FC, ReactNode } from 'react'

import { cva } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'
import { match } from 'ts-pattern'

import {
  IconTriangleDown,
  IconTriangleLeft,
  IconTriangleRight,
  IconTriangleUp,
} from '~/components/ui/icons'

const drawerVariants = cva('fixed transform transition-transform', {
  variants: {
    position: {
      // h-[calc(50%+30px)] の30pxはボタンの高さ、translate-y-[calc(100%-30px-48px-8px)]の30pxはボタンの高さ、64pxは飛び出している要素の大きさ
      bottom:
        'bottom-0 left-0 data-[state=closed]:translate-y-[calc(100%-30px-64px)] data-[state=open]:translate-y-0 w-full h-[calc(50%+30px)]',
      // w-[calc(50%+32px)] の32pxはボタンの幅、translate-x-[calc(100%-32px)] の32pxはボタンの幅
      right:
        'top-0 right-0 data-[state=closed]:translate-x-[calc(100%-32px)] data-[state=open]:translate-x-0 h-full w-[calc(25%+32px)] min-w-[calc(375px+32px)]',
      left: 'top-0 left-0 data-[state=closed]:translate-x-[calc(-100%+32px)] data-[state=open]:translate-x-0 h-full w-[calc(25%+32px)] min-w-[calc(375px+32px)]',
      full: 'top-0 left-0 w-full h-full data-[state=closed]:translate-y-full data-[state=open]:translate-y-0',
    },
  },
  defaultVariants: {
    position: 'bottom',
  },
})

const buttonVariants = cva('block bg-white text-brand border-grayBorder', {
  variants: {
    position: {
      bottom: 'mx-auto rounded-t px-6 py-2 border-b',
      right: 'my-auto rounded-l px-2 py-6 border-r',
      left: 'my-auto rounded-r px-2 py-6 border-r',
      full: 'hidden',
    },
  },
  defaultVariants: {
    position: 'bottom',
  },
})

const containerVariants = cva('flex', {
  variants: {
    position: {
      bottom: 'flex-col w-full h-full',
      right: 'flex-row h-full w-full',
      left: 'flex-row h-full w-full',
      full: 'flex-col w-full h-full',
    },
  },
  defaultVariants: {
    position: 'bottom',
  },
})

type DrawerPosition = 'bottom' | 'right' | 'left' | 'full'

const getIconTriangle = (isOpen: boolean, position: DrawerPosition) => {
  return match(position)
    .with('bottom', () => (isOpen ? <IconTriangleDown /> : <IconTriangleUp />))
    .with('right', () =>
      isOpen ? (
        <IconTriangleRight className="h-[30px] w-[16px]" />
      ) : (
        <IconTriangleLeft className="h-[30px] w-[16px]" />
      ),
    )
    .with('left', () =>
      isOpen ? (
        <IconTriangleLeft className="h-[30px] w-[16px]" />
      ) : (
        <IconTriangleRight className="h-[30px] w-[16px]" />
      ),
    )
    .otherwise(() => {})
}

type ContentsProps = {
  children: ReactNode
  position: DrawerPosition
  isOpen: boolean
  toggleIsOpen: () => void
}

const Contents = ({
  children,
  position,
  isOpen,
  toggleIsOpen,
}: ContentsProps) => {
  const IconTriangle = getIconTriangle(isOpen, position)

  return match(position)
    .with('left', () => (
      <>
        {children}
        <button className={buttonVariants({ position })} onClick={toggleIsOpen}>
          {IconTriangle || null}
        </button>
      </>
    ))
    .otherwise(() => (
      <>
        <button className={buttonVariants({ position })} onClick={toggleIsOpen}>
          {IconTriangle || null}
        </button>
        {children}
      </>
    ))
}

type DrawerProps = {
  isOpen: boolean
  toggleIsOpen: () => void
  position?: DrawerPosition
  children: ReactNode
  className?: string
}

export const Drawer: FC<DrawerProps> = ({
  isOpen,
  toggleIsOpen,
  position = 'bottom',
  children,
  className,
}) => {
  return (
    <div
      className={twMerge(drawerVariants({ position }), className)}
      data-state={isOpen ? 'open' : 'closed'}
    >
      <div className={containerVariants({ position })}>
        <Contents
          isOpen={isOpen}
          position={position}
          toggleIsOpen={toggleIsOpen}
        >
          {children}
        </Contents>
      </div>
    </div>
  )
}
